import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import crmService from "../../crmConfig.json";
import { crmAxios } from "../../app/crmApi";
import { errorToast, successToast } from "../../helpers/toaster";

const initialState = {
  agents: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  selectedAgent: null,
  documents: null,
  dashboard: null,
  loading: false,
  actionLoading: false,
  creating: false,
};

export const fetchAgentsAsync = createAsyncThunk(
  "crmAdmin/fetchAgents",
  async (params, { rejectWithValue }) => {
    try {
      const res = await crmAxios.get(crmService.adminAgents, { params });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const createAgentAsync = createAsyncThunk(
  "crmAdmin/createAgent",
  async (formData, { rejectWithValue }) => {
    try {
      const res = await crmAxios.post(crmService.adminAgents, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const fetchAgentByIdAsync = createAsyncThunk(
  "crmAdmin/fetchAgentById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await crmAxios.get(`${crmService.adminAgents}/${id}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

export const fetchAgentDocumentsAsync = createAsyncThunk(
  "crmAdmin/fetchAgentDocuments",
  async (id, { rejectWithValue }) => {
    try {
      const res = await crmAxios.get(
        `${crmService.adminAgents}/${id}/documents`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

const agentAction = (name, method, suffix) =>
  createAsyncThunk(name, async ({ id, ...body }, { rejectWithValue }) => {
    try {
      const url = `${crmService.adminAgents}/${id}${suffix}`;
      const res = await crmAxios[method](url, body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  });

export const approveAgentAsync = agentAction(
  "crmAdmin/approveAgent",
  "patch",
  "/approve"
);
export const rejectAgentAsync = agentAction(
  "crmAdmin/rejectAgent",
  "patch",
  "/reject"
);
export const activateAgentAsync = agentAction(
  "crmAdmin/activateAgent",
  "patch",
  "/activate"
);
export const deactivateAgentAsync = agentAction(
  "crmAdmin/deactivateAgent",
  "patch",
  "/deactivate"
);
export const resetAgentPasswordAsync = agentAction(
  "crmAdmin/resetAgentPassword",
  "post",
  "/reset-password"
);

export const fetchAdminDashboardAsync = createAsyncThunk(
  "crmAdmin/fetchDashboard",
  async (_, { rejectWithValue }) => {
    try {
      const res = await crmAxios.get(crmService.adminDashboard);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data);
    }
  }
);

const applyAgentUpdate = (state, payload) => {
  if (!payload?.data?.id) return;
  state.agents = state.agents.map((a) =>
    a.id === payload.data.id ? payload.data : a
  );
  if (state.selectedAgent?.id === payload.data.id) {
    state.selectedAgent = payload.data;
  }
};

const isActionThunk = (thunks, phase) => (action) =>
  thunks.some((thunk) => action.type === thunk[phase].type);

const mutatingThunks = [
  approveAgentAsync,
  rejectAgentAsync,
  activateAgentAsync,
  deactivateAgentAsync,
  resetAgentPasswordAsync,
];
const agentUpdatingThunks = [
  approveAgentAsync,
  rejectAgentAsync,
  activateAgentAsync,
  deactivateAgentAsync,
];

const crmAdminSlice = createSlice({
  name: "crmAdmin",
  initialState,
  reducers: {
    clearSelectedAgent: (state) => {
      state.selectedAgent = null;
      state.documents = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAgentsAsync.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload?.success) {
          state.agents = payload.data.agents;
          state.pagination = payload.data.pagination;
        }
      })
      .addCase(fetchAgentsAsync.rejected, (state, { payload }) => {
        state.loading = false;
        errorToast(payload?.message || "Failed to load agents");
      })
      .addCase(createAgentAsync.pending, (state) => {
        state.creating = true;
      })
      .addCase(createAgentAsync.fulfilled, (state, { payload }) => {
        state.creating = false;
        if (payload?.success) {
          successToast(payload.message);
          state.agents = [payload.data, ...state.agents];
        } else {
          errorToast(payload?.message || "Failed to create agent");
        }
      })
      .addCase(createAgentAsync.rejected, (state, { payload }) => {
        state.creating = false;
        errorToast(payload?.message || "Failed to create agent");
      })
      .addCase(fetchAgentByIdAsync.fulfilled, (state, { payload }) => {
        if (payload?.success) state.selectedAgent = payload.data;
      })
      .addCase(fetchAgentDocumentsAsync.fulfilled, (state, { payload }) => {
        if (payload?.success) state.documents = payload.data;
      })
      .addCase(fetchAdminDashboardAsync.fulfilled, (state, { payload }) => {
        if (payload?.success) state.dashboard = payload.data;
      })
      .addCase(resetAgentPasswordAsync.fulfilled, (state, { payload }) => {
        state.actionLoading = false;
        if (payload?.success) successToast(payload.message);
        else errorToast(payload?.message || "Failed to reset password");
      })
      .addMatcher(isActionThunk(mutatingThunks, "pending"), (state) => {
        state.actionLoading = true;
      })
      .addMatcher(
        isActionThunk(agentUpdatingThunks, "fulfilled"),
        (state, { payload }) => {
          state.actionLoading = false;
          if (payload?.success) {
            successToast(payload.message);
            applyAgentUpdate(state, payload);
          } else {
            errorToast(payload?.message || "Action failed");
          }
        }
      )
      .addMatcher(
        isActionThunk(mutatingThunks, "rejected"),
        (state, { payload }) => {
          state.actionLoading = false;
          errorToast(payload?.message || "Action failed");
        }
      );
  },
});

export const { clearSelectedAgent } = crmAdminSlice.actions;

export const selectAgents = (state) => state.crmAdmin.agents;
export const selectAgentsPagination = (state) => state.crmAdmin.pagination;
export const selectAgentsLoading = (state) => state.crmAdmin.loading;
export const selectSelectedAgent = (state) => state.crmAdmin.selectedAgent;
export const selectAgentDocuments = (state) => state.crmAdmin.documents;
export const selectAdminDashboard = (state) => state.crmAdmin.dashboard;
export const selectActionLoading = (state) => state.crmAdmin.actionLoading;
export const selectCreatingAgent = (state) => state.crmAdmin.creating;

export default crmAdminSlice.reducer;
